import { useEffect, useState } from 'react';
import { Loader2, Save, RefreshCw, Handshake } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';

const LANGS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
];

export default function ManufacturerAgreementTemplateEditor() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tpl, setTpl] = useState({ version: 1, titles: { fr: '', en: '', zh: '' }, contents: { fr: '', en: '', zh: '' } });
  const [activeLang, setActiveLang] = useState('fr');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/ma-template');
      setTpl(res.data);
    } catch (e) { toast({ title: 'Erreur', description: 'Impossible de charger le modèle.', duration: 10000 }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put('/admin/ma-template', {
        title_fr: tpl.titles.fr, title_en: tpl.titles.en, title_zh: tpl.titles.zh,
        content_fr: tpl.contents.fr, content_en: tpl.contents.en, content_zh: tpl.contents.zh,
      });
      setTpl(res.data);
      toast({ title: 'Nouvelle version enregistrée', description: `Modèle manufacturier v${res.data.version} (FR/EN/ZH). Les accords déjà acceptés conservent leur version.`, duration: 10000 });
    } catch (e) { toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Sauvegarde impossible.', duration: 10000 }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6" data-testid="ma-template-editor">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Handshake className="w-5 h-5 text-brand-navy" />
          <h2 className="font-bold text-brand-navy">Modèle d'accord manufacturier · trilingue</h2>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-navy text-white font-bold uppercase tracking-wider">v{tpl.version}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-brand-navy text-xs font-semibold hover:border-brand-navy">
            <RefreshCw className="w-3.5 h-3.5" /> Recharger
          </button>
          <button data-testid="save-ma-template" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-yellow text-brand-navy text-xs font-bold hover:bg-yellow-400 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Enregistrer les 3 langues
          </button>
        </div>
      </div>

      <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 mb-4">
        <b>Brouillon :</b> Chaque manufacturier reçoit son accord dans une seule langue choisie au moment de l'envoi. Éditez chaque version indépendamment. Les accords déjà signés conservent la version qu'ils ont signée.
      </div>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {LANGS.map((l) => (
          <button
            key={l.code}
            onClick={() => setActiveLang(l.code)}
            data-testid={`ma-tab-${l.code}`}
            className={`px-4 py-2 text-sm font-bold border-b-2 -mb-px transition ${activeLang === l.code ? 'border-brand-yellow text-brand-navy' : 'border-transparent text-gray-400 hover:text-brand-navy'}`}
          >
            {l.flag} {l.label}
          </button>
        ))}
      </div>

      <label className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-1">Titre ({activeLang.toUpperCase()})</label>
      <input
        value={tpl.titles[activeLang] || ''}
        onChange={(e) => setTpl({ ...tpl, titles: { ...tpl.titles, [activeLang]: e.target.value } })}
        className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-brand-navy mb-4"
      />

      <label className="block text-xs font-semibold text-brand-navy uppercase tracking-wider mb-1">Contenu HTML ({activeLang.toUpperCase()})</label>
      <textarea
        value={tpl.contents[activeLang] || ''}
        onChange={(e) => setTpl({ ...tpl, contents: { ...tpl.contents, [activeLang]: e.target.value } })}
        rows={20}
        className="w-full px-3 py-2 border border-gray-200 rounded font-mono text-xs leading-relaxed focus:outline-none focus:border-brand-navy"
      />
    </div>
  );
}
